import React, { FC, useReducer, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, useFormik } from 'formik';
import { Box, Grid, Typography } from '@material-ui/core';
import { cloneDeep } from 'lodash';
import moment from 'moment';

import {
  ContentFragment,
  ContentInput,
  Network,
  PostType,
  ShareContentMutation,
  ShareContentMutationVariables,
  SharedStatus,
  ShareType,
  useManualPostMutation,
  useShareContentMutation,
} from 'api';
import { ContentProvider } from 'providers';
import { useCapabilities, useNotify, useProtectedClient } from 'hooks';
import { FormikNotifier } from 'components';
import { ComposeLayout, ComposeTabBody } from 'features/compose';
import {
  ExternalShareOption,
  UploadSection,
  initialPostUIState,
  postContextReducer,
  PostUIContext,
  UrlField,
  UrlPreview,
  PhotoStrip,
} from 'features/posting';
import * as paths from 'paths';

import { Share } from '../types';
import { FormButtons, ShareForm, Tabs } from '../components/ExternalShare';

/**
 * An external share creates a post and then a share. The process is transparent
 * to the user. As a result, we pull from multiple sections of the codebase and
 * use all sorts of poor React practices to make this work. This code is a prime
 * example of an API smell, and please don't use these patterns elsewhere
 */
const ExternalShare: FC = () => {
  const notify = useNotify();
  const client = useProtectedClient();
  const location = useLocation();
  const history = useHistory();
  const { isAdmin, isModerator } = useCapabilities();

  const { t } = useTranslation();
  const [state, dispatch] = useReducer(postContextReducer, initialPostUIState);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const initialPostFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: {
      group_ids: ['0'],
      content_id: '',
      content_source_id: 'manual',
      content_object: {
        post_type: PostType.Quickshare,
        url: '',
        body: '',
        photos: [],
        media: null,
        url_metadata_override: {
          title: '',
          description: '',
        },
      },
    },
  });

  const initialShareFormikRef = useFormik({
    onSubmit: () => undefined,
    initialValues: {
      items: [],
    },
  });

  const postFormikRef = useRef<FormikProps<ContentInput>>(initialPostFormikRef);
  const shareFormikRef = useRef<FormikProps<Share>>(initialShareFormikRef);

  const { mutate: shareContent, isLoading: isShareContentLoading } = useShareContentMutation({
    onError: notify.mutationError,
    onSuccess: handleCompleted,
  });

  const { mutate: processContent, isLoading: isProcessContentLoading } = useManualPostMutation({
    onError: notify.mutationError,
    onSuccess: ({ processContent }) => {
      shareToTwitterAndLinkedin(processContent);
      shareToFacebook(processContent);
    },
  });

  const isLoading = isShareContentLoading || isProcessContentLoading;

  function handleCompleted(data: ShareContentMutation) {
    const { results } = data.shareContent;
    const hasFailure = results.some(item => item.status === SharedStatus.Failed);

    if (hasFailure) {
      notify.error({ i18n: { i18nKey: 'sharing:shareFailure' } });
      return;
    }

    const [shareItem] = shareFormikRef.current.values.items;
    const { share_type, share_at } = shareItem;

    let i18nKey = 'sharing:shareSuccess';
    const date = moment.unix(share_at).format('dddd, MMMM Do, YYYY [at] h:mma');

    switch (share_type) {
      case ShareType.Auto:
        i18nKey = 'sharing:autoSuccess';
        break;
      case ShareType.Scheduled:
        i18nKey = 'sharing:scheduledSuccess';
        break;
      case ShareType.Now:
        i18nKey = 'sharing:shareSuccess';
        break;
      default:
        throw new Error('Missing ShareType implementation');
    }

    notify.info({ i18n: { i18nKey, values: { date } } });
    location.key ? history.goBack() : history.push(paths.index);
  }

  function shareToTwitterAndLinkedin(content: ContentFragment) {
    const clonedItems = cloneDeep(shareFormikRef.current.values.items);
    const twitterAndLinkedInItems = clonedItems
      .filter(item => item.state.active)
      .filter(
        item =>
          item.state.networkMeta.provider === Network.Twitter ||
          item.state.networkMeta.provider === Network.Linkedin ||
          item.state.networkMeta.provider === Network.LinkedinPage
      );

    const photosToPost = content?.content_object?.photos ?? [];

    twitterAndLinkedInItems.forEach(item => {
      // Remove client side state
      delete item.state;

      // Explicitly pass photos from created content
      item.photos = photosToPost;
    });

    if (!twitterAndLinkedInItems.length) {
      return;
    }

    const variables = {
      timezone: shareFormikRef.current.values.timezone,
      content_id: content.content_id,
      items: twitterAndLinkedInItems,
    };

    shareContent(variables as ShareContentMutationVariables);
  }

  // We don't have tracking for Facebook posts
  function shareToFacebook(content: ContentFragment) {
    const clonedItems = cloneDeep(shareFormikRef.current.values.items);
    const facebookShareItem = clonedItems.find(item => {
      return item.state.networkMeta.provider === Network.Facebook;
    });

    if (!facebookShareItem?.state?.active) {
      return;
    }

    const fbUIVariables = {
      method: 'share',
      href: `https://${client.subdomain}/content/${content.content_id}`,
      quote: facebookShareItem.user_commentary,
    };

    delete facebookShareItem.state;

    window?.FB?.ui(fbUIVariables);
  }

  function handleSubmit() {
    processContent({ content: postFormikRef.current.values });
  }

  function renderLink() {
    return (
      <ComposeTabBody>
        <UrlField />
        {state.validUrl && <UrlPreview moderator={isAdmin || isModerator} />}
      </ComposeTabBody>
    );
  }

  function renderImage() {
    const { values } = postFormikRef.current;
    const { photos } = values.content_object;

    return (
      <ComposeTabBody>
        <UploadSection />
        {!!photos.length && (
          <Box marginBottom={-1}>
            <PhotoStrip />
          </Box>
        )}
      </ComposeTabBody>
    );
  }

  return (
    <PostUIContext.Provider value={{ state, dispatch }}>
      <ContentProvider value={postFormikRef.current.values}>
        <ComposeLayout
          header={
            <Grid item>
              <Typography variant="h4">{t('sharing:shareToYourNetworks')}</Typography>
            </Grid>
          }
          tabs={<Tabs />}
          body={
            <Formik
              validateOnChange={false}
              onSubmit={() => undefined}
              initialValues={postFormikRef.current.initialValues}
              innerRef={ref => (postFormikRef.current = ref)}
            >
              <FormikNotifier onChange={forceUpdate}>
                {state.shareOption === ExternalShareOption.LinkShare && renderLink()}
                {state.shareOption === ExternalShareOption.ImageShare && renderImage()}
              </FormikNotifier>
            </Formik>
          }
        >
          <ShareForm onUpdate={forceUpdate} ref={shareFormikRef} />
          <Grid item>
            <FormButtons
              loading={isLoading}
              onSubmit={handleSubmit}
              postFormikProps={postFormikRef.current}
              shareFormikProps={shareFormikRef.current}
            />
          </Grid>
        </ComposeLayout>
      </ContentProvider>
    </PostUIContext.Provider>
  );
};

export default ExternalShare;
